import React, { useState, useEffect, useRef } from 'react';

const VenueSelector = ({ onVenueSelect, selectedVenue, placeholder = "Search for a venue..." }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [predictions, setPredictions] = useState([]);
  const [showPredictions, setShowPredictions] = useState(false);
  const [loading, setLoading] = useState(false);
  const autocompleteSuggestion = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    // Initialize Google Places services - use legacy API for now as it's more reliable
    const initializeGooglePlaces = () => {
      if (window.google && window.google.maps && window.google.maps.places) {
        autocompleteSuggestion.current = new window.google.maps.places.AutocompleteService();
      } else {
        // Retry after a short delay in case the API is still loading
        setTimeout(initializeGooglePlaces, 100);
      }
    };

    initializeGooglePlaces();
  }, []);

  useEffect(() => {
    if (selectedVenue) {
      setSearchTerm(selectedVenue.name || '');
    }
  }, [selectedVenue]);

  const handleInputChange = (value) => {
    setSearchTerm(value);
    
    if (!value.trim() || !autocompleteSuggestion.current) {
      setPredictions([]);
      setShowPredictions(false);
      return;
    }

    setLoading(true);
    
    // Detect if user is searching for a city/zip code or a venue
    const isZipCode = /^\d{5}(-\d{4})?$/.test(value.trim());
    const isCityState = /^[a-zA-Z\s]+,\s*[A-Z]{2}$/.test(value.trim());
    const isCitySearch = value.toLowerCase().includes('atlanta') || value.toLowerCase().includes('ga') || value.toLowerCase().includes('georgia');
    
    let request;
    if (isZipCode || isCityState || isCitySearch) {
      // For zip codes, cities, and state searches, use geocode only
      request = {
        input: value,
        types: ['geocode'],
        componentRestrictions: { country: 'us' }
      };
    } else {
      // For venue searches, use establishment only
      request = {
        input: value,
        types: ['establishment'],
        componentRestrictions: { country: 'us' }
      };
    }

    // Use legacy API (AutocompleteService) - more reliable
    autocompleteSuggestion.current.getPlacePredictions(request, (predictions, status) => {
      setLoading(false);
      if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
        setPredictions(predictions);
        setShowPredictions(true);
      } else {
        setPredictions([]);
        setShowPredictions(false);
      }
    });
  };

  const handlePredictionSelect = (prediction) => {
    // Use legacy PlacesService - more reliable
    fetchPlaceDetailsLegacy(prediction);
  };

  const fetchPlaceDetailsLegacy = (prediction) => {
    const placesService = new window.google.maps.places.PlacesService(
      document.createElement('div')
    );

    placesService.getDetails(
      {
        placeId: prediction.place_id,
        fields: ['name', 'formatted_address', 'geometry', 'place_id', 'types', 'website', 'formatted_phone_number']
      },
      (place, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
          const venueData = {
            id: place.place_id,
            name: place.name,
            address: place.formatted_address,
            location: {
              lat: place.geometry.location.lat(),
              lng: place.geometry.location.lng()
            },
            types: place.types,
            website: place.website,
            phone: place.formatted_phone_number,
            googlePlaceId: place.place_id
          };
          
          onVenueSelect(venueData);
          setSearchTerm(place.name);
          setShowPredictions(false);
          setPredictions([]);
        } else {
          // Fallback to basic prediction data if place details fail
          const venueData = {
            id: prediction.place_id,
            name: prediction.structured_formatting.main_text,
            address: prediction.structured_formatting.secondary_text,
            location: null,
            types: [],
            website: null,
            phone: null,
            googlePlaceId: prediction.place_id
          };
          
          onVenueSelect(venueData);
          setSearchTerm(prediction.structured_formatting.main_text);
          setShowPredictions(false);
          setPredictions([]);
        }
      }
    );
  };

  const handleInputFocus = () => {
    if (predictions.length > 0) {
      setShowPredictions(true);
    }
  };

  const handleInputBlur = () => {
    // Delay hiding predictions to allow for clicks
    setTimeout(() => {
      setShowPredictions(false);
    }, 200);
  };

  const clearVenue = () => {
    setSearchTerm('');
    setPredictions([]);
    setShowPredictions(false);
    onVenueSelect(null);
  };

  return (
    <div className="relative">
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={(e) => handleInputChange(e.target.value)}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          placeholder={placeholder}
          className="w-full p-3 bg-gray-700 text-white rounded border border-gray-600 focus:border-green-500 focus:outline-none pr-10"
        />
        
        {loading && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-500"></div>
          </div>
        )}
        
        {selectedVenue && !loading && (
          <button
            onClick={clearVenue}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
          >
            ✕
          </button>
        )}
      </div>

      {/* Predictions Dropdown */}
      {showPredictions && predictions.length > 0 && (
        <div className="absolute z-[9999] w-full mt-1 bg-gray-800 border-2 border-green-500 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {predictions.map((prediction) => (
            <button
              key={prediction.place_id}
              onClick={() => handlePredictionSelect(prediction)}
              className="w-full text-left p-3 hover:bg-gray-700 text-white border-b border-gray-600 last:border-b-0 transition-colors"
            >
              <div className="font-medium">{prediction.structured_formatting.main_text}</div>
              <div className="text-sm text-gray-400">{prediction.structured_formatting.secondary_text}</div>
            </button>
          ))}
        </div>
      )}

      {/* No Results */}
      {showPredictions && predictions.length === 0 && searchTerm.trim() && !loading && (
        <div className="absolute z-50 w-full mt-1 bg-gray-800 border border-gray-600 rounded-lg shadow-lg p-3">
          <div className="text-gray-400 text-center">No venues found</div>
        </div>
      )}
    </div>
  );
};

export default VenueSelector; 